<template>
  <div v-if="!(field.hideEmpty && !pages.length && !editable)" :ref="inputRef" class="py-2">
    <v-row align="center">
      <v-col>
        <label :class="!editable ? 'read-only-label' : ''">
          {{ field.label }} {{ field.required ? '*' : '' }}
          <small v-if="documentObject.id && documentSize">
            ({{ documentSize | formatBytes }})
          </small>
        </label>
      </v-col>
      <span v-if="!documentObject.id && documentType?.isRequired"
            class="text-caption red--text mr-2">
        * Documento Obrigatório
      </span>
      <reg-dynamic-menu v-if="documentObject.id && field.canDelete"
                        :options="[
                          { id: 'excluir', title: 'Excluir', icon: 'mdi-delete', disabled: false, visible: true },
                          { id: 'imprimir', title: 'Imprimir', icon: 'mdi-printer', disabled: false, visible: true }
                        ]"
                        @excluir="doDeleteDocument"
                        @imprimir="onDocumentPrint" />
      <reg-dynamic-menu v-else-if="documentObject.id"
                        :options="[
                          { id: 'imprimir', title: 'Imprimir', icon: 'mdi-printer', disabled: false, visible: true }
                        ]"
                        @imprimir="onDocumentPrint" />
    </v-row>
    <v-card tile color="elevation-0 file-card">
      <v-card-text class="py-2 pr-3 pl-2">
        <v-row>
          <v-col cols="12" class="pa-0">
            <reg-document-pages v-if="editable || pages.length"
                                :document-id="documentObject.id"
                                :document-name="documentTypeName"
                                :pages="pages"
                                :show-delete="editable"
                                @delete="doDeleteDocumentPage">
              <template v-if="!field.maxPages || pages.length < field.maxPages" #action>
                <v-col cols="auto">
                  <reg-upload-button v-if="editable"
                                     :multiple="true"
                                     :label="$t('select_file')"
                                     :allowed-extensions="field.allowedExtensions"
                                     color="primary"
                                     icon="mdi-file-plus"
                                     @files-selected="onFilesSelected">
                    <template #default="{ selectFiles }">
                      <v-btn fab
                             color="primary"
                             small
                             class="my-2"
                             @click="selectFiles">
                        <v-icon dark>
                          mdi-plus
                        </v-icon>
                      </v-btn>
                    </template>
                  </reg-upload-button>
                </v-col>
              </template>
            </reg-document-pages>

            <v-row v-if="!editable && !pages.length" justify="center" class="ma-2">
              {{ $t('no_file_registered') }}
            </v-row>
            <v-col v-if="editable && field.hint" cols="12">
              <p class="grey--text mb-0">
                {{ field.hint }}
              </p>
            </v-col>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance, toRef } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useDynamicField } from '../composables/useDynamicField'
import { useDownload } from '../composables/useDownload'
import UploadButton from '~sendData/UploadButton.vue'
import DocumentPages from '~shared/DocumentPages.vue'
import DynamicMenu from '~shared/DynamicMenu.vue'
import { isEmpty, cloneDeep, map } from 'lodash'

export default defineComponent({
  name: 'DynamicFileField',

  components: {
    'reg-upload-button': UploadButton,
    'reg-document-pages': DocumentPages,
    'reg-dynamic-menu': DynamicMenu
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },

  emits: ['input', 'change', 'file-selected', 'document-deleted'],

  setup(props, { emit, expose }) {
    const router = useRouter()
    const instance = getCurrentInstance()
    const $store = instance?.proxy?.$store
    const $t = instance?.proxy?.$t || ((key) => key)
    const uploadService = instance?.proxy?.$upload
    const confirmService = instance?.proxy?.$confirm
    const toasted = instance?.proxy?.$toasted
    const loggedUser = instance?.proxy?.$loggedUser
    const hasAccess = instance?.proxy?.$hasAccess

    const {
      fieldValue,
      setFieldValue,
      inputRef,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: {},
      emit
    })

    const { getSignedS3Url } = useDownload()

    // State
    const documentObject = ref({})
    const filesUploader = ref({})

    // Computed properties
    const selectedProject = computed(() => $store.state.projectsModule.selectedProject)
    const documentTypes = computed(() => $store.state.documentsModule.documentTypes)

    const pages = computed(() => {
      return (documentObject.value.pages || []).filter((page) => !page.deleted)
    })

    const documentSize = computed(() => {
      if (documentObject.value.kilobytes) {
        return documentObject.value.kilobytes * 1000
      }
      return 0
    })

    const documentName = computed(() => {
      if (documentObject.value.name) {
        return documentObject.value.name
      }
      return ''
    })

    const documentType = computed(() => {
      return documentTypes.value.find((documentType) => {
        return documentType.id === props.field.documentTypeId
      })
    })

    const documentTypeName = computed(() => {
      return documentType.value?.name || ''
    })

    const editable = computed(() => {
      return !props.readOnly && !props.field.readOnly
    })

    // Methods
    const deleteDocument = (payload) => {
      $store.dispatch('deleteDocument', payload)
    }

    const updateDocumentAndCleanAi = (payload) => {
      $store.dispatch('updateDocumentAndCleanAi', payload)
    }

    const startUpload = () => {
      if (!isEmpty(filesUploader.value)) {
        filesUploader.value.startUpload()
      }
    }

    const cancelUpload = () => {
      if (!isEmpty(filesUploader.value)) {
        filesUploader.value.cancelAll()
      }
    }

    const createFileUploader = () => {
      if (!props.field.uploadBasePath) {
        throw new Error('uploadBasePath is required')
      }

      if (!props.field.uploadCreateFunction) {
        throw new Error('uploadCreateFunction is required')
      }

      if (isEmpty(filesUploader.value)) {
        filesUploader.value = uploadService?.getUploader({
          multiple: true,
          basePath: props.field.uploadBasePath,
          sizeLimit: props.field.uploadSizeLimit || 1024 * 1024 * 1024,
          autoStart: false,
          context: cloneDeep(props.field.uploadContext || {})
        })
        filesUploader.value.$on('all-upload-complete', onAllFilesUploadComplete)
      }
    }

    const onFilesSelected = async (e) => {
      let lastPage = documentObject.value.pages.length
      const pages = map(e.files, (file) => {
        lastPage += 1
        return {
          name: file.name,
          kilobytes: file.size / 1000,
          type: file.type,
          page: lastPage,
          file
        }
      })

      documentObject.value.pages = documentObject.value.pages.concat(pages)

      const files = e.files

      createFileUploader()

      for (let i = 0; i < files.length; i++) {
        files[i].documentTypeId = props.field.documentTypeId
      }

      await filesUploader.value.addFiles(files, true).then((filesInfos) => {
        documentObject.value.pages.forEach((page) => {
          const fileInfo = filesInfos.find((fileInfo) => {
            return fileInfo.file === page.file
          })
          if (fileInfo) {
            page.id = fileInfo.uuid
            page.url = fileInfo.fullPath
            page.width = fileInfo.width
            page.height = fileInfo.height
          }
        })

        emit('file-selected', documentObject.value)
      })
    }

    const cancelFileUpload = (file) => {
      if (!isEmpty(filesUploader.value)) {
        const uploads = filesUploader.value.getUploads().filter((upload) => upload.status === 'submitted')
        const fileId = (uploads.find((fileInfo) => {
          return fileInfo.file === file
        }) || {}).id
        if (fileId !== undefined) {
          filesUploader.value.cancel(fileId)
        }
      }
    }

    const onAllFilesUploadComplete = async (e) => {
      const { succeeded } = e
      const fileInfos = []
      for (const fileId of succeeded) {
        const fileInfo = await filesUploader.value.getFileInfo(fileId)
        fileInfos.push(fileInfo)
      }

      documentObject.value.pages.forEach((page) => {
        if (!page.file) return // Already uploaded

        const fileInfo = fileInfos.find((fileInfo) => {
          return fileInfo.file === page.file
        })
        if (fileInfo) {
          delete page.file
        } else {
          page.deleted = true // Error on upload
        }
      })

      if (documentObject.value.id) {
        updateDocumentAndCleanAi({
          id: documentObject.value.id,
          pages: documentObject.value.pages
        })
      } else {
        const context = fileInfos?.[0]?.context || props.field.context || {}

        const contextedDocument = {
          ...documentObject.value,
          ...context
        }
        props.field.uploadCreateFunction(contextedDocument)
      }

      filesUploader.value = null
    }

    const updateFieldValue = () => {
      if (isEmpty(fieldValue.value)) {
        documentObject.value = {
          documentTypeId: props.field.documentTypeId,
          pages: []
        }
      } else {
        documentObject.value = cloneDeep(fieldValue.value)
      }
    }

    const doDeleteDocumentPage = (page) => {
      if (documentObject.value.pages.filter((p) => !p.deleted).length === 1) {
        doDeleteDocument()
        return
      }

      const message = 'Deseja realmente excluir esta página?'
      const documentPageIndex = documentObject.value.pages.findIndex((p) => p.page === page.page)

      if (page.file) { // Contains the local selected File
        cancelFileUpload(page.file)
        documentObject.value.pages.splice(documentPageIndex, 1)
      } else {
        confirmService({
          title: $t('warning'),
          message,
          confirmText: $t('yes'),
          cancelText: $t('no')
        }).then((confirmed) => {
          if (!confirmed) return
          documentObject.value.pages[documentPageIndex].deleted = true
          documentObject.value.pages[documentPageIndex].deletedBy = loggedUser.id
          documentObject.value.pages[documentPageIndex].deletedAt = new Date().toISOString()
          // Vue 2.7 reactivity
          documentObject.value = { ...documentObject.value }
          updateDocumentAndCleanAi({
            id: documentObject.value.id,
            pages: documentObject.value.pages
          }).then(() => {
            toasted.success($t('success_delete_page'))
          })
        })
      }
    }

    const resetUploader = () => {
      if (!isEmpty(filesUploader.value)) {
        filesUploader.value.cancelAll()
      }
      setFieldValue({})
    }

    const doDeleteDocument = () => {
      const message = 'Deseja realmente excluir este documento e TODAS as suas páginas?'
      const docId = documentObject.value.id
      if (!docId) {
        resetUploader()
      } else if (docId) {
        confirmService({
          title: $t('warning'),
          message,
          confirmText: $t('yes'),
          cancelText: $t('no')
        }).then((confirmed) => {
          if (!confirmed) return
          deleteDocument({
            id: docId,
            deletedAt: new Date().toISOString(),
            deletedBy: loggedUser.id
          }).then(() => {
            toasted.success($t('success_delete_file'))
            emit('document-deleted', docId)
          })
          setFieldValue({})
        })
      }
    }

    const onDocumentPrint = () => {
      const routeData = router.resolve({
        name: 'document-print',
        params: {
          documentId: documentObject.value.id
        },
        query: {
          projectId: selectedProject.value.id
        }
      })
      window.open(routeData.href, '_blank')
    }

    const validate = () => {
      if (documentType.value?.isRequired && !documentObject.value.id && !documentObject.value.pages.length) {
        return false
      }
      return true
    }

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate
    })

    // Watchers
    watch(fieldValue, () => {
      updateFieldValue()
    })

    // Lifecycle
    onMounted(() => {
      updateFieldValue()
    })

    return {
      documentObject,
      filesUploader,
      pages,
      documentSize,
      documentName,
      documentType,
      documentTypeName,
      editable,
      startUpload,
      cancelUpload,
      onFilesSelected,
      doDeleteDocumentPage,
      doDeleteDocument,
      onDocumentPrint,
      validate,
      inputRef
    }
  }
})
</script>

<style lang="scss">
  .read-only-label {
    font-size: 1.1em;
  }

  .btn-rounded {
    border-radius: 10px;
  }

</style>
